import React, { useState } from 'react';
import { ChevronDown, ChevronUp, HelpCircle, ShoppingBag, Home, Repeat, Wrench, Shield, CreditCard, MessageCircle, AlertTriangle } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const FAQPage = () => {
  const [openSection, setOpenSection] = useState(null);
  const [openQuestion, setOpenQuestion] = useState(null);

  const faqSections = [
    {
      id: 'selling',
      title: 'Selling on Your Buy Site',
      icon: ShoppingBag,
      color: 'from-blue-500 to-indigo-600',
      questions: [
        {
          q: 'How do I create a listing?',
          a: 'To create a listing, click "Get Started" or go to your dashboard and select "Create Listing". Fill in the details including title, description, price, category, and upload photos. Your listing will be live immediately after submission.'
        },
        {
          q: 'Is it free to list items?',
          a: 'Yes! Basic listings are completely free. You can promote your listing to reach more buyers by using our optional boost features, which start at just $5 for 7 days of increased visibility.'
        },
        {
          q: 'How do I price my items?',
          a: 'Research similar items on the marketplace to get an idea of fair pricing. You can set a fixed price, enable price negotiation, or for auctions, set a starting bid. Remember to factor in any OB (government tax) if applicable.'
        },
        {
          q: 'What is OB and when should I include it?',
          a: 'OB (Omzetbelasting) is the sales tax in the ABC Islands (6% in Curaçao and Bonaire, 1.5% BBO in Aruba). Business sellers should indicate whether their price includes or excludes OB. Private sellers typically don\'t charge OB.'
        },
        {
          q: 'How do I get paid?',
          a: 'Your Buy Site is a platform that connects buyers and sellers. Payment is arranged directly between you and the buyer. We recommend cash on pickup, bank transfer, or using secure payment apps. Never share sensitive financial information.'
        },
        {
          q: 'Can I edit or delete my listing?',
          a: 'Yes, you can edit or delete your listings anytime from your dashboard. Go to "My Listings", find the item, and click Edit or Delete. Changes are reflected immediately.'
        }
      ]
    },
    {
      id: 'renting',
      title: 'Renting & Leasing',
      icon: Home,
      color: 'from-emerald-500 to-teal-600',
      questions: [
        {
          q: 'How do rental listings work?',
          a: 'Rental listings work similarly to sales listings. Specify your rental price (daily, weekly, or monthly), deposit requirements, and rental terms. Interested renters will contact you to arrange viewings and agreements.'
        },
        {
          q: 'What should I include in a rental listing?',
          a: 'Include clear photos, accurate description, rental price and period, deposit amount, availability dates, location, amenities (for real estate), and any rules or requirements. The more details, the better quality inquiries you\'ll receive.'
        },
        {
          q: 'How do I handle rental agreements?',
          a: 'We recommend creating a written rental agreement that includes: rental period, price, deposit, payment schedule, rules, and termination conditions. For real estate, consider using a notary for formal contracts.'
        },
        {
          q: 'What about security deposits?',
          a: 'Security deposits are common for rentals. Clearly state the deposit amount in your listing. Keep records of the item\'s condition before and after rental. Return deposits promptly if no damage occurred.'
        }
      ]
    },
    {
      id: 'trading',
      title: 'Trading & Exchanges',
      icon: Repeat,
      color: 'from-purple-500 to-pink-600',
      questions: [
        {
          q: 'How does trading work on Your Buy Site?',
          a: 'Select "Trade" as your listing type and describe what you\'re offering and what you\'re looking for in exchange. Interested traders will contact you to negotiate. You can trade items of similar or different values.'
        },
        {
          q: 'Can I trade partially with cash?',
          a: 'Yes! Many trades involve a combination of items and cash to balance the value. Clearly communicate your expectations in the listing description or during negotiations.'
        },
        {
          q: 'How do I ensure a fair trade?',
          a: 'Research the market value of both items. Meet in person to inspect items before finalizing. Document the trade agreement. Meet in public places for safety. Trust your instincts - if something feels off, walk away.'
        }
      ]
    },
    {
      id: 'services',
      title: 'Offering Services',
      icon: Wrench,
      color: 'from-amber-500 to-orange-600',
      questions: [
        {
          q: 'How do I list my services?',
          a: 'Choose the appropriate service category (Painters, Electricians, Plumbers, etc.), describe your services, set your hourly or project rate, and add photos of your work. Include your experience and qualifications.'
        },
        {
          q: 'Should I charge by hour or by project?',
          a: 'It depends on the service type. Hourly rates work well for maintenance and repair work. Fixed project rates are better for defined jobs like painting a room or installing fixtures. Be clear about what\'s included.'
        },
        {
          q: 'How do I build trust as a service provider?',
          a: 'Create a complete profile with photos of your work, respond promptly to inquiries, be punctual, deliver quality work, and ask satisfied customers to leave reviews. A business account with verification adds credibility.'
        }
      ]
    },
    {
      id: 'safety',
      title: 'Safety & Security',
      icon: Shield,
      color: 'from-red-500 to-rose-600',
      questions: [
        {
          q: 'How do I stay safe when meeting buyers/sellers?',
          a: 'Always meet in public places during daylight hours. Bring a friend if possible. Trust your instincts. For high-value items, consider meeting at a police station or bank. Never share personal financial information.'
        },
        {
          q: 'How do I avoid scams?',
          a: 'Be wary of buyers who offer more than asking price, request unusual payment methods, or pressure you to act quickly. Never ship items before receiving payment. Verify checks have cleared before releasing items.'
        },
        {
          q: 'What if I have a problem with a transaction?',
          a: 'Try to resolve issues directly with the other party first. Document all communications. If you believe you\'ve been scammed, report it to local police and contact us through the Help Center.'
        },
        {
          q: 'How does Your Buy Site protect my information?',
          a: 'We take privacy seriously. Your contact information is only shared when you choose to respond to inquiries. We never sell your data to third parties. See our Privacy Policy for complete details.'
        }
      ]
    },
    {
      id: 'payments',
      title: 'Payments & Promotions',
      icon: CreditCard,
      color: 'from-cyan-500 to-blue-600',
      questions: [
        {
          q: 'How do promoted listings work?',
          a: 'Promoted listings appear at the top of search results and in the "Featured Deals" section. Choose from Basic ($5/7 days), Standard ($10/14 days), or Premium ($25/30 days) packages for maximum visibility.'
        },
        {
          q: 'What payment methods are accepted for promotions?',
          a: 'We accept major credit cards, debit cards, and iDEAL through our secure payment processor Stripe. All transactions are encrypted and secure.'
        },
        {
          q: 'Can I get a refund on promotions?',
          a: 'Promotions are non-refundable once activated. However, if you sell your item before the promotion period ends, the remaining time cannot be transferred or refunded.'
        }
      ]
    }
  ];

  const toggleSection = (sectionId) => {
    setOpenSection(openSection === sectionId ? null : sectionId);
    setOpenQuestion(null);
  };

  const toggleQuestion = (questionIndex) => {
    setOpenQuestion(openQuestion === questionIndex ? null : questionIndex);
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      
      <main className="pt-24 pb-16">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-4xl">
          {/* Header */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-blue-500 to-indigo-600 mb-6">
              <HelpCircle className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-foreground mb-4">
              Frequently Asked Questions
            </h1>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Everything you need to know about buying, selling, renting, and trading on Your Buy Site
            </p>
          </div>

          {/* FAQ Sections */}
          <div className="space-y-4">
            {faqSections.map((section) => {
              const Icon = section.icon;
              const isOpen = openSection === section.id;
              
              return (
                <div key={section.id} className="rounded-2xl border border-border overflow-hidden bg-card">
                  {/* Section Header */}
                  <button
                    onClick={() => toggleSection(section.id)}
                    className="w-full flex items-center justify-between p-6 hover:bg-muted/50 transition-colors"
                    data-testid={`faq-section-${section.id}`}
                  >
                    <div className="flex items-center gap-4">
                      <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${section.color} flex items-center justify-center`}>
                        <Icon className="w-6 h-6 text-white" />
                      </div>
                      <span className="text-xl font-semibold text-foreground">{section.title}</span>
                    </div>
                    {isOpen ? (
                      <ChevronUp className="w-6 h-6 text-muted-foreground" />
                    ) : (
                      <ChevronDown className="w-6 h-6 text-muted-foreground" />
                    )}
                  </button>

                  {/* Questions */}
                  {isOpen && (
                    <div className="border-t border-border">
                      {section.questions.map((item, index) => {
                        const questionKey = `${section.id}-${index}`;
                        const isQuestionOpen = openQuestion === questionKey;
                        
                        return (
                          <div key={index} className="border-b border-border last:border-b-0">
                            <button
                              onClick={() => toggleQuestion(questionKey)}
                              className="w-full flex items-center justify-between p-5 pl-8 hover:bg-muted/30 transition-colors text-left"
                            >
                              <span className="font-medium text-foreground pr-4">{item.q}</span>
                              {isQuestionOpen ? (
                                <ChevronUp className="w-5 h-5 text-muted-foreground flex-shrink-0" />
                              ) : (
                                <ChevronDown className="w-5 h-5 text-muted-foreground flex-shrink-0" />
                              )}
                            </button>
                            {isQuestionOpen && (
                              <div className="px-8 pb-5 text-muted-foreground leading-relaxed">
                                {item.a}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Contact Section */}
          <div className="mt-12 p-8 rounded-2xl bg-gradient-to-br from-blue-500/10 to-indigo-500/10 border border-blue-500/20 text-center">
            <MessageCircle className="w-12 h-12 text-blue-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-foreground mb-2">Still have questions?</h2>
            <p className="text-muted-foreground mb-6">
              Can't find what you're looking for? Our support team is here to help.
            </p>
            <a
              href="mailto:support@yourbuysite.com"
              className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-blue-500 to-indigo-600 text-white font-semibold rounded-full hover:shadow-lg transition-all"
            >
              Contact Support
            </a>
          </div>

          {/* Disclaimer */}
          <div className="mt-8 p-6 rounded-xl bg-amber-500/10 border border-amber-500/20">
            <div className="flex items-start gap-4">
              <AlertTriangle className="w-6 h-6 text-amber-500 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="font-semibold text-foreground mb-2">Tax Disclaimer</h3>
                <p className="text-sm text-muted-foreground">
                  Your Buy Site is purely a platform connecting buyers and sellers. We are not a financial system or tax advisor. 
                  All users are responsible for organizing their own taxes correctly, including OB (sales tax), income tax, and any 
                  other applicable taxes. Consult with a local tax professional for guidance specific to your situation.
                </p>
              </div>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default FAQPage;
